/**
 * Authentic Evminov Exercise Protocols & Clinical Photo Gallery
 * Extracted and adapted from authentic Evminov Clinical Archives (evminov.shop, evminovusa.com, evminovcanada.com, evminovsboard.kyiv.ua)
 */

export const exerciseGalleryData = [
  {
    id: 'family-prone-extension',
    title: 'Assisted Prone Spinal Extension & Pediatric Posture',
    category: 'family',
    categoryLabel: 'Family & Youth Scoliosis',
    zone: 'Thoracolumbar Spine & Scapular Stabilizers',
    angle: '10° – 15° Low Incline',
    image: '/images/gallery/family-prone-extension.jpg',
    thumbAspect: '4 / 3',
    mechanism: 'Unloads axial gravity from the spinal column while activating dorsal extensor muscles without lumbar hyperextension.',
    description: 'Safe for children and adolescents correcting early-stage scoliosis or slouching. A parent or partner provides light counter-stability at the feet while the child extends arms forward along the compliant pine board.',
    steps: [
      'Position chest flat against the smooth wooden board at a 10°–15° angle.',
      'Grasp carriage handles with palms down, keeping chin lightly tucked.',
      'Extend one or both arms forward in line with spine, lifting upper chest by 1-2 inches.',
      'Hold for 3-5 seconds while exhaling smoothly; repeat 8-10 cycles.',
    ],
    duration: '5 – 8 Minutes',
    benefit: 'Scoliosis prevention & back muscle tone',
  },
  {
    id: 'family-ball-decompression',
    title: 'Dynamic Ball Ergonomics on Low-Incline Plane',
    category: 'family',
    categoryLabel: 'Family & Youth Scoliosis',
    zone: 'Full Spinal Axis & Core Coordination',
    angle: '10° – 12° Incline',
    image: '/images/gallery/family-ball-decompression.jpg',
    thumbAspect: '4 / 3',
    mechanism: 'Combines axial traction with playful proprioceptive motor coordination to engage deep core stabilizers.',
    description: 'Developed in the Evminov Kyiv Spine Center for children with postural asymmetries. Using a lightweight ball encourages thoracic rotation and abdominal engagement while the spine remains safely decompressed.',
    steps: [
      'Lie supine on the board with knees gently bent.',
      'Take lightweight medicine/exercise ball from partner overhead.',
      'Slowly pass the ball back while engaging lower transversus abdominis.',
      'Maintains zero axial compression throughout the full range of motion.',
    ],
    duration: '6 – 10 Minutes',
    benefit: 'Neuromuscular coordination & fun rehab',
  },
  {
    id: 'family-home-lifestyle',
    title: 'Multi-Generational Home Spine Hygiene',
    category: 'family',
    categoryLabel: 'Family & Youth Scoliosis',
    zone: 'Whole Spine (All Ages)',
    angle: 'Universal (8° – 45°)',
    image: '/images/gallery/family-home-lifestyle.jpg',
    thumbAspect: '3 / 4',
    mechanism: 'Adjustable aluminum carriage accommodates heights from 4\'2" to 6\'8", serving children, parents, and grandparents on one single device.',
    description: 'Vyacheslav Evminov designed the board to be the "Toothbrush for the Spine" — a daily 15-minute hygiene ritual shared by the entire household to prevent degenerative disc disease before symptoms appear.',
    steps: [
      'Adjust carriage height pin to match user body length.',
      'Select customized angle based on age and clinical goal (10° for seniors, 20° for active adults).',
      'Perform 10-15 minutes of rhythmic stretching and restorative breathing.',
    ],
    duration: '15 Minutes Daily',
    benefit: 'Lifelong family disc protection',
  },
  {
    id: 'youth-paired-traction',
    title: 'Youth Paired Decompression & Posture Reset',
    category: 'family',
    categoryLabel: 'Family & Youth Scoliosis',
    zone: 'Thoracic Rib Cage & Spinal Column',
    angle: '12° – 16° Incline',
    image: '/images/gallery/youth-paired-traction.jpg',
    thumbAspect: '4 / 3',
    mechanism: 'Symmetrical upper body reach relieves unilateral muscle spasm and retrains spinal symmetry during growth spurts.',
    description: 'Teenagers carrying heavy backpacks and looking down at smartphones develop forward head posture and compensatory spinal curves. This paired stretch re-establishes upright spinal alignment naturally.',
    steps: [
      'Lie face down with hands resting evenly on the carriage handles.',
      'Elongate the neck while pulling shoulder blades down toward back pockets.',
      'Hold position for 30–45 seconds with calm diaphragmatic breaths.',
    ],
    duration: '7 – 10 Minutes',
    benefit: 'Counters backpack slump & tech-neck',
  },
  {
    id: 'lumbar-single-leg-lift',
    title: 'Unilateral Lumbar Decompression & Sciatic Floss',
    category: 'lumbar',
    categoryLabel: 'Lumbar & Disc Relief',
    zone: 'L4-L5, L5-S1 & Sciatic Nerve Pathway',
    angle: '15° – 20° Incline',
    image: '/images/gallery/lumbar-single-leg-lift.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Gentle downward gravity traction opens intervertebral foramina while single-leg flexion mobilizes the sciatic nerve root without impingement.',
    description: 'One of the most prescribed clinical protocols for acute sciatica. The patient holds the upper carriage while bending one knee, creating asymmetrical decompression that releases tension on the piriformis and nerve sheath.',
    steps: [
      'Lie face up with arms extended holding upper carriage grips.',
      'Slowly slide one foot up along the polished wooden plane until knee is bent at 90°.',
      'Keep pelvis resting flat against the board; do not arch lower back.',
      'Hold for 5 deep breaths, then smoothly extend leg and alternate sides.',
    ],
    duration: '8 – 12 Minutes',
    benefit: 'Instant sciatica & disc bulge relief',
  },
  {
    id: 'hamstring-pelvic-stretch',
    title: 'Hamstring-Pelvic Restorative Traction',
    category: 'lumbar',
    categoryLabel: 'Lumbar & Disc Relief',
    zone: 'Posterior Chain & Sacroiliac (SI) Joint',
    angle: '15° – 22° Incline',
    image: '/images/gallery/hamstring-pelvic-stretch.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Decompresses the lumbosacral junction while extending tight hamstrings that pull the pelvis out of neutral alignment.',
    description: 'Tight hamstrings are a leading cause of flat-back syndrome and disc herniation. This exercise allows full hamstring lengthening while the spine is supported and unweighted by the wooden board.',
    steps: [
      'Grasp carriage with palms facing inward, allowing spine to elongate.',
      'Bring both knees toward chest, then gently extend heels toward ceiling.',
      'Feel the deep stretch through calves, hamstrings, and lower back without strain.',
      'Lower feet back down slowly in a 4-second eccentric descent.',
    ],
    duration: '8 – 10 Minutes',
    benefit: 'Pelvic realignment & disc decompression',
  },
  {
    id: 'lumbar-axial-extension',
    title: 'Prone Hip Extension & Facet Joint Opening',
    category: 'lumbar',
    categoryLabel: 'Lumbar & Disc Relief',
    zone: 'Lumbar Spine & Anterior Psoas',
    angle: '15° – 20° Incline',
    image: '/images/gallery/lumbar-axial-extension.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Lengthens chronically shortened hip flexors (psoas) that pull lumbar vertebrae forward, while gentle traction widens facet joints.',
    description: 'Performed face down on the board. Holding the carriage grips overhead, the user gently bends one knee to 90 degrees and lifts the thigh 1 inch off the board, stimulating the deep multifidus muscle.',
    steps: [
      'Lie prone on the board, gripping handles with comfortable elbow flexion.',
      'Bend one knee upward at a right angle.',
      'Perform micro-lifts of the knee (0.5 to 1 inch) without tilting pelvis.',
      'Perform 10 smooth pulses per leg to trigger fluid circulation in lumbar discs.',
    ],
    duration: '6 – 8 Minutes',
    benefit: 'Psoas release & facet joint decompression',
  },
  {
    id: 'cervical-traction-incline',
    title: 'Upper Thoracic & Scapular Release',
    category: 'cervical',
    categoryLabel: 'Cervical & Upper Spine',
    zone: 'C6-T4, Rhomboids & Trapezius',
    angle: '12° – 18° Incline',
    image: '/images/gallery/cervical-traction-incline.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Overhead traction stretches tightened pectoral and subclavius fascia, reversing rounded shoulders and upper back hunching.',
    description: 'Designed for office workers and drivers who suffer from burning pain between the shoulder blades. Gravity gently pulls the torso downward while the hands remain anchored on the carriage.',
    steps: [
      'Secure hands on high carriage grips with palms relaxed.',
      'Allow gravity to slowly draw your ribcage downward away from your shoulders.',
      'Inhale deeply into the upper chest, expanding ribs laterally.',
      'Exhale slowly, feeling the intervertebral spaces between T1 and T6 open.',
    ],
    duration: '5 – 7 Minutes',
    benefit: 'Counters thoracic stiffness & kyphosis',
  },
  {
    id: 'alternating-leg-traction',
    title: 'Bilateral Alternating Leg Pedaling in Traction',
    category: 'core',
    categoryLabel: 'Athletic Core & Stability',
    zone: 'Transverse Abdominis & Deep Multifidus',
    angle: '18° – 25° Incline',
    image: '/images/gallery/alternating-leg-traction.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Activates the deep abdominal girdle under continuous axial traction, strengthening core muscles without disc compression.',
    description: 'Traditional floor bicycle crunches generate dangerous shear force on vulnerable lumbar discs. On the Evminov board, your spine is in traction throughout the entire cycling movement, making it 100% safe.',
    steps: [
      'Grasp carriage handles firmly above head while lying on your back.',
      'Lift both legs into a 90° tabletop position.',
      'Slowly alternate extending one leg forward while drawing the other toward chest.',
      'Execute 16–20 smooth, deliberate pedal strokes matching breath tempo.',
    ],
    duration: '8 – 12 Minutes',
    benefit: 'Safe core conditioning & disc stability',
  },
  {
    id: 'lumbar-lordosis-roll',
    title: 'Anatomical Lordosis Support & Imbibition',
    category: 'lumbar',
    categoryLabel: 'Lumbar & Disc Relief',
    zone: 'L1-L5 Natural Lumbar Arch',
    angle: '10° – 15° Gentle Incline',
    image: '/images/gallery/lumbar-lordosis-roll.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Specialized orthopedic foam cushion under the lumbar spine restores natural curvature while gentle traction pulls discs apart.',
    description: 'Many patients lose their natural inward lower back curve due to prolonged sitting. Adding the Evminov lumbar roll re-educates the spinal ligaments and facilitates fluid imbibition into flattened discs.',
    steps: [
      'Place the cylindrical lumbar roll under your lower back curve.',
      'Lie back relaxed with arms overhead holding carriage softly.',
      'Allow gravity and the curved support to mold the spine into anatomical neutral.',
      'Rest passively for 6-8 minutes, letting discs absorb surrounding nutrient fluids.',
    ],
    duration: '6 – 8 Minutes',
    benefit: 'Restores lumbar lordosis & rehydrates discs',
  },
  {
    id: 'glisson-carriage-suspension',
    title: 'Inverted Foot-Lock Decompression with Carriage Handles',
    category: 'core',
    categoryLabel: 'Athletic Core & Stability',
    zone: 'Lumbosacral Junction & Hip Flexors',
    angle: '20° – 30° Incline',
    image: '/images/gallery/glisson-carriage-suspension.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Reverse axial traction allows lower body weight to decompress lumbar vertebrae from the bottom up, without ankle pinch.',
    description: 'Using the soft padded foot attachments on the carriage handles, the user locks their feet and lies in reverse orientation. This produces targeted lumbosacral traction with zero neck strain or head pressure.',
    steps: [
      'Hook heels securely into the padded foot sleeves on the carriage.',
      'Slowly lower body down the wooden board into head-down incline.',
      'Rest hands across chest or gently overhead.',
      'Feel the deep release in L5-S1 and SI joints; hold for 3-5 minutes.',
    ],
    duration: '5 – 8 Minutes',
    benefit: 'Deep lower back release & zero head pressure',
  },
  {
    id: 'abdominal-decompression-crunch',
    title: 'Zero-Compression Abdominal Decompression Crunch',
    category: 'core',
    categoryLabel: 'Athletic Core & Stability',
    zone: 'Rectus Abdominis & Deep Core',
    angle: '15° – 25° Incline',
    image: '/images/gallery/abdominal-decompression-crunch.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Isolates abdominal muscle contraction while maintaining lower back stabilization along the board flex-zone.',
    description: 'Traditional sit-ups impose up to 730 lbs of compressive force on lumbar discs. Performing crunches on the inclined Evminov board isolates the abdominals while the pelvic anchor prevents lumbar hyperextension.',
    steps: [
      'Lock feet in foot holders with knees bent.',
      'Place fingertips behind ears or across chest; avoid pulling on neck.',
      'Curl upper spine off board by 3-4 inches, focusing on abdominal contraction.',
      'Inhale slowly as you lower back down to full spinal alignment.',
    ],
    duration: '8 – 10 Minutes',
    benefit: 'Builds iron core without spinal wear',
  },
  {
    id: 'latissimus-girdle-traction',
    title: 'Latissimus Dorsi & Shoulder Girdle Elongation',
    category: 'cervical',
    categoryLabel: 'Cervical & Upper Spine',
    zone: 'Thoracolumbar Fascia & Latissimus',
    angle: '15° – 22° Incline',
    image: '/images/gallery/latissimus-girdle-traction.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Full overhead arm extension on incline unweights shoulder joints and decompresses the mid-thoracic spine.',
    description: 'Ideal for swimmers, gymnasts, and desk workers who experience shoulder impingement and mid-back tightness. The grip height can be calibrated on the carriage to match arm length.',
    steps: [
      'Grasp carriage with palms facing downward.',
      'Allow body weight to slide downward 1-2 inches until comfortable traction is felt.',
      'Perform micro-side tilts of the ribcage to stretch the left and right latissimus.',
      'Hold each side for 15 seconds with calm breaths.',
    ],
    duration: '6 – 8 Minutes',
    benefit: 'Opens shoulder joint capsules & thoracic spine',
  },
  {
    id: 'incline-glute-bridge',
    title: 'Incline Spinal Bridge & Sacroiliac Alignment',
    category: 'core',
    categoryLabel: 'Athletic Core & Stability',
    zone: 'Gluteus Maximus, Hamstrings & SI Joint',
    angle: '15° – 20° Incline',
    image: '/images/gallery/incline-glute-bridge.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Lifting pelvis on an inclined plane fires glutes while keeping cervical and thoracic spine in safe low-gravity traction.',
    description: 'Weak glutes cause excessive pelvic tilt and lower back fatigue. The inclined bridge activates the gluteal complex while the head and upper back remain decompressed on the smooth pine plane.',
    steps: [
      'Lie supine on board holding upper carriage loosely.',
      'Plant feet flat with knees bent.',
      'Drive through heels to lift hips until thighs and torso form a straight line.',
      'Squeeze glutes at the top for 2 seconds, then lower vertebrae down one by one.',
    ],
    duration: '8 – 10 Minutes',
    benefit: 'Strengthens glutes & aligns pelvis',
  },
  {
    id: 'supine-spinal-elongation',
    title: 'Total Body Supine Decompression & Pumping',
    category: 'lumbar',
    categoryLabel: 'Lumbar & Disc Relief',
    zone: 'Full Spinal Column (Atlas to Coccyx)',
    angle: '12° – 20° Incline',
    image: '/images/gallery/supine-spinal-elongation.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'The baseline posture of the Evminov Method: creates negative pressure inside discs, triggering the spinal pumping mechanism (diffusive imbibition).',
    description: 'This is the universal exercise prescribed to every new patient. Lying comfortably along the grain of the resonant pine board, gravity elongates all 23 intervertebral discs without discomfort or danger.',
    steps: [
      'Take hold of upper carriage handles with relaxed shoulders.',
      'Extend legs straight down the center of the board.',
      'Take long, deep breaths into the lower abdomen; feel the spine elongate by up to 0.5 inch.',
      'Perform gentle 5-degree foot flexions to pump fluid through the spinal column.',
    ],
    duration: '10 – 15 Minutes',
    benefit: 'Rehydrates discs & eliminates nerve pinches',
  },
  {
    id: 'glisson-cervical-neutral',
    title: 'Glisson Loop Neutral Cervical Alignment',
    category: 'cervical',
    categoryLabel: 'Cervical & Upper Spine',
    zone: 'Occiput, Atlas C1 & Axis C2',
    angle: '8° – 12° Gentle Incline',
    image: '/images/gallery/glisson-cervical-neutral.jpg',
    thumbAspect: '4 / 3',
    mechanism: 'Gentle 8-12 lb tensile traction safely expands cervical disc spaces, relieving vertebral artery pressure and occipital headaches.',
    description: 'Features the original European CE-certified Glisson loop. Specially padded with skin-friendly cotton and memory foam, it cups the chin and occiput to gently decompress the upper neck.',
    steps: [
      'Fit padded Glisson loop snugly under chin and back of skull.',
      'Attach harness carabiner to top bracket of the Evminov board.',
      'Lie comfortably on the low incline, feeling gentle upward cervical traction.',
      'Relax neck muscles completely for 5-8 minutes; no sudden turns.',
    ],
    duration: '5 – 8 Minutes',
    benefit: 'Relieves chronic migraines & neck stiffness',
  },

  {
    id: 'thoracic-opening-stretch',
    title: 'Thoracic Heart-Opening & Rib Mobility',
    category: 'cervical',
    categoryLabel: 'Cervical & Upper Spine',
    zone: 'T1-T12 Vertebrae & Intercostal Muscles',
    angle: '15° – 20° Incline',
    image: '/images/gallery/thoracic-opening-stretch.jpg',
    thumbAspect: '4 / 3',
    mechanism: 'Anatomical flexion of the natural pine board gently pushes stiff thoracic vertebrae forward into healthy extension.',
    description: 'Reverses the hunchback curve caused by long hours in front of laptops. The compliant elasticity of the pine board yields just enough to stretch tight chest muscles without bruising.',
    steps: [
      'Rest mid-back over the primary flex zone of the board.',
      'Extend arms out into a wide "Y" or "W" shape.',
      'Take 10 expansive breaths into the rib cage, feeling chest muscles open.',
    ],
    duration: '5 – 8 Minutes',
    benefit: 'Expands lung capacity & reverses hunching',
  },
  {
    id: 'incline-reverse-crunch',
    title: 'Incline Knee-to-Chest Lumbar Stretch',
    category: 'core',
    categoryLabel: 'Athletic Core & Stability',
    zone: 'Lumbosacral Junction & Deep Hip Flexors',
    angle: '15° – 22° Incline',
    image: '/images/gallery/incline-reverse-crunch.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Unweights lower vertebrae while gently lengthening tight lumbar erector spinae muscles.',
    description: 'A classic decompression movement from the Evminov Rehabilitation Center in Kyiv. Draws the knees gently upward toward the chest, flattening the lower back against the compliant wood to widen the intervertebral spaces.',
    steps: [
      'Hold carriage firmly overhead.',
      'Draw both knees toward chest in a smooth, continuous curl.',
      'Hold at peak for 2 seconds while exhaling completely.',
      'Slowly extend legs back down along the board.',
    ],
    duration: '7 – 10 Minutes',
    benefit: 'Decompresses L4-S1 & eases back spasms',
  },
  {
    id: 'lordosis-passive-traction',
    title: 'Passive Gravity Imbibition Relaxation',
    category: 'lumbar',
    categoryLabel: 'Lumbar & Disc Relief',
    zone: 'Dehydrated Discs (All Segments)',
    angle: '10° – 14° Incline',
    image: '/images/gallery/lordosis-passive-traction.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Post-exercise rest locks in rehydrated disc fluid volume through osmotic pressure equilibrium.',
    description: 'Every session on the Evminov board concludes with 3-5 minutes of completely passive traction. This allows the spinal discs to retain the nutrient fluids absorbed during the pumping movements.',
    steps: [
      'Release all muscular tension in neck, shoulders, and legs.',
      'Close eyes and breathe with slow diaphragmatic cycles.',
      'Remain still for 3 to 5 minutes before slowly rising.',
    ],
    duration: '5 Minutes',
    benefit: 'Locks in disc hydration & resets nervous system',
  },
  {
    id: 'spinal-alignment-supine',
    title: 'Postural Realignment & Neuromuscular Reset',
    category: 'family',
    categoryLabel: 'Family & Youth Scoliosis',
    zone: 'Scoliotic Curves & Postural Symmetry',
    angle: '12° – 18° Incline',
    image: '/images/gallery/spinal-alignment-supine.jpg',
    thumbAspect: '16 / 9',
    mechanism: 'Symmetrical tactile feedback from flat resonant pine plane retrains brain proprioception for upright posture.',
    description: 'When people slouch, their brain begins to perceive asymmetry as "straight". The flat reference plane of the Evminov board resets proprioceptors in the spinal cord, restoring natural alignment.',
    steps: [
      'Lie flat along the centerline of the board.',
      'Check that shoulders, hips, and heels are equidistant from board edges.',
      'Hold symmetrical posture for 5 minutes to reset body balance.',
    ],
    duration: '5 – 8 Minutes',
    benefit: 'Brain posture recalibration & alignment',
  }
];

export const biomechanicsPrinciples = [
  {
    title: 'The Spinal Pumping Mechanism (Diffusive Imbibition)',
    subtitle: 'Natural Fluid Hydration & Intervertebral Disc Pumping',
    text: 'Adult intervertebral discs have zero direct blood vessels and rely 100% on fluid exchange through diffusion. Under low-angle incline (10°–25°), axial spinal load drops by 70%, creating negative intradiscal pressure. Combined with micro-movements of deep short muscles (multifidus), this triggers a natural pumping mechanism that pulls nutrient-rich synovial fluid into compressed, dehydrated discs.',
    badge: 'Core Clinical Principle',
  },
  {
    title: 'The "Toothbrush for the Spine" Philosophy',
    subtitle: '15-Minute Daily Preventive Hygiene for Disc Preservation',
    text: 'Everyone brushes their teeth twice a day to prevent cavities. Your spine carries your entire body weight every waking hour, yet most people do nothing for its daily hygiene until a debilitating disc herniation strikes. 15 minutes of daily decompression on the Evminov board is baseline hygiene that keeps discs plump, hydrated, and immune to degeneration.',
    badge: 'Vyacheslav Evminov Heritage',
  },
  {
    title: 'Deep Short Spinal Muscles vs. Superficial Muscles',
    subtitle: 'Targeting Intersegmental Multifidi Without Axial Strain',
    text: 'Standard gym weightlifting and crunches engage superficial global muscles, compressing the spinal column with hundreds of pounds of pressure. The Evminov Method isolates the deep intersegmental rotatores and multifidi while the spine is unweighted, strengthening the anatomical muscular corset that permanently supports the vertebrae.',
    badge: 'Anatomical Superiority',
  }
];

